import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  Button,
  Input,
  Modal,
  ModalProps,
  Row,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  DataGrid,
  Loading,
  ScreenMask,
  SetDefaultWarehouseDialog,
  SiteContent,
  message,
} from 'components/common';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  DEFAULT_US_DATE_FORMAT,
  HTTP_STATUS_OK,
  LOADING_ICON_SIZE1,
  LS_DEFAULT_WAREHOUSE_KEY,
} from 'constants/config';
import { RootState } from 'reducers';
import { downloadBulkReturnFromErp, searchBulkReturnFromErp } from 'services/return';
import {
  convertTimeByUtc,
  dateSorter,
  getWarehouseCodeFromCache,
} from 'utils';

type Props = ModalProps & {
  customer: StringKAnyVPair,
};

const SearchErpDialog = (props: Props) => {
  const DEFAULT_GRID_PAGE_SIZE = 20;
  const { useRef, useState } = React;
  const [erpDataList, setErpDataList] = useState<StringKAnyVPair[]>([]);
  const [gridCount, setGridCount] = useState(0);
  const [inited, setInited] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [defaultWarehouseVisible, setDefaultWarehouseVisible] = useState(false);
  const [dialogClosable, setDialogClosable] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<StringKAnyVPair>({});

  const gridPageIndex = useRef<number>(0);
  const gridPageSize = useRef<number>(DEFAULT_GRID_PAGE_SIZE);
  const searchCodeTimer = useRef<any>(null);

  const warehouseList = useSelector(
    (state: RootState) => state.admin.warehouses,
    shallowEqual
  );

  const columns = [
    {
      header: 'Create Date',
      name: 'returnTime',
      sort: dateSorter,
      render(row: any) {
        const { data } = row;

        return convertTimeByUtc(data.returnTime, '', '', DEFAULT_US_DATE_FORMAT);
      },
    },
    {
      header: 'ERP Return #',
      name: 'customerReturnNum',
    },
    {
      header: 'Reference #',
      name: 'returnRefNum',
    },
    {
      header: 'Customer Name',
      name: 'customerName',
    },
    {
      header: 'Invoice #',
      //name: 'customerName',
    },
    {
      header: 'Action',
      headerAlign: 'start',
      maxWidth: 120,
      //name: 'customerName',
      sortable: false,
      textAlign: 'end',
      render(row: any) {
        const { data } = row;

        return (
          <Button
            onClick={() => downloadErpReturnItem(data)}
            size="small"
          >
            <DownloadOutlined />
            Download
          </Button>
        );
      },
    },
  ];

  const downloadErpReturnItem = async (rd: StringKAnyVPair) => {
    //console.log('d', rd);
    setLockScreen(true);
    try {
      const res = await downloadBulkReturnFromErp({
        customerReturnUuid: rd.customerReturnUuid,
        warehouseCode: selectedWarehouse.warehouseCode,
      });
      let downloaded = false;

      console.log('r', res);
      if (res && typeof res === 'object') {
        if (res.code === HTTP_STATUS_OK) {
          downloaded = true;
        }
      }

      if (downloaded) {
        message.success({
          content: 'Downloaded successfully from ERP',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        searchErp();
      } else {
        message.error({
          content: 'Download from ERP failed',
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
      message.error({
        content: `Downloaded from ERP error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  const getErpGridDataList = (eds: StringKAnyVPair[]) => {
    return eds.map(e => {
      return {
        ...e.customerReturnDto,
      };
    });
  };

  const onCancel = (evt: any) => {
    props.onCancel && props.onCancel(evt);
  };

  const onLimitChange = (limit: number) => {
    //gridRef?.current.deselectAll();
    if (gridPageSize.current !== limit) {
      if (gridPageIndex.current * limit >= gridCount) gridPageIndex.current = 0;

      gridPageSize.current = limit;
      //loadTransferList();
    }
  };

  const onSkipChange = (skip: number) => {
    const idx = skip / gridPageSize.current;
    //gridRef?.current.deselectAll();
    //console.log('s', skip, idx);
    if (gridPageIndex.current !== idx) {
      gridPageIndex.current = idx;
      //loadTransferList();
    }
  };

  const onSearchKeyUp = (evt: any) => {
    const val = evt.target.value;

    if (searchCodeTimer.current) {
      clearTimeout(searchCodeTimer.current);
      searchCodeTimer.current = null;
    }

    if ([9, 13].indexOf(evt.keyCode) < 0 && val.trim().length > 2) {
      searchCodeTimer.current = setTimeout(() => {
        searchErp(val.trim());
      }, 1000);
    }
  };

  const openSetDefaultWarehouseDialog = (config: StringKAnyVPair) => {
    setDialogClosable(
      typeof config.closable === 'boolean' ? config.closable : true
    );
    setDefaultWarehouseVisible(true);
  };

  const searchErp = async (sCode = '') => {
    console.log('s', sCode);
    setLockScreen(true);
    try {
      const { count, data} = await searchBulkReturnFromErp(
        {customerCode: props.customer.value, warehouseNum: selectedWarehouse.warehouseNum},
        {pageIndex: gridPageIndex.current, pageSize: gridPageSize.current},
      );

      if (Array.isArray(data)) {
        //console.log('count', count, data);
        setErpDataList(data);
        setGridCount(count > data.length ? count : data.length);
      }
    } catch(e) {
      message.error({
        content: `Searched from ERP error: ${e}`,
        duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
      });
    } finally {
      setLockScreen(false);
    }
  };

  React.useEffect(() => {
    if (!inited) {
      const whid = Number(localStorage.getItem(LS_DEFAULT_WAREHOUSE_KEY));

      if (!whid || (typeof whid === 'number' && !getWarehouseCodeFromCache(whid))) {
        openSetDefaultWarehouseDialog({ closable: false });
      }

      const wh = warehouseList.find(w => w.id === whid);

      if (wh) {
        //dispatch({ type: PRAction.SetSelectedWarehouse, params: {...wh} });
        setSelectedWarehouse(wh);
      }

      setInited(true);
    }
  }, [
    inited,
    warehouseList,
  ]);

  return (<>
    <Modal
      {...props}
      footer={null}
      onCancel={onCancel}
      //title={dialogTitle()}
      visible={props.visible}
      //width={dialogWidth()}
    >
      <SiteContent className="common-content">
        <Row>
          <Input.Search
            allowClear
            onKeyUp={onSearchKeyUp}
            onSearch={searchErp}
            placeholder="Input search content here"
            size="large"
          />
        </Row>
        <div className="mt-16">
          <DataGrid
            columns={columns}
            count={gridCount}
            dataSource={getErpGridDataList(erpDataList)}
            limit={gridPageSize.current}
            onLimitChange={onLimitChange}
            onSkipChange={onSkipChange}
            pageSizes={[5, 20, 50, 100, 200]}
            pagination
            rowKey="customerReturnUuid"
            //rowClassName={getTransferRowClass}
            style={{height: 'calc(100vh - 240px)', maxHeight: 600}}
          />
        </div>
      </SiteContent>
    </Modal>
    {defaultWarehouseVisible && (
      <SetDefaultWarehouseDialog
        closable={dialogClosable}
        close={() => {
          setDefaultWarehouseVisible(false);
        }}
        visible={defaultWarehouseVisible}
      />
    )}
    {lockScreen && (
      <ScreenMask>
        <Loading size={LOADING_ICON_SIZE1} />
      </ScreenMask>
    )}
  </>);
};

export default SearchErpDialog;
